import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, ChevronUp, Plus, Trash2, Camera, Check, UploadCloud, X, File, FileVideo, Image as ImageIcon } from 'lucide-react';
import { Button, TableActionButton } from '../../../../components/ui';
import { settingsRoomService } from '../../../verve-settings/services/settingsRoomService';
import AddRoomModal from '../../../verve-settings/modals/AddRoomModal';
import { BASE_URL } from '../../../../lib/API';
import toast from 'react-hot-toast';

// Custom Accordion Component
const Section = ({ title, expanded, onToggle, children }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden bg-white mb-2 shadow-sm">
    <div 
      className="px-3 py-2.5 bg-slate-50/80 cursor-pointer flex justify-between items-center transition-colors hover:bg-slate-100/80"
      onClick={onToggle}
    >
      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{title}</span>
      {expanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
    </div>
    {expanded && (
      <div className="p-3 border-t border-slate-200 space-y-3 bg-white">
        {children}
      </div>
    )}
  </div>
);

const MeasurementSection = ({ title, expandedSection, toggleSection, data, sectionKey, updateNested, children }) => {
  const updateMeas = (idx, key, value) => {
    const newMeas = [...(data.measurements || [])];
    if (!newMeas[idx]) newMeas[idx] = {};
    newMeas[idx][key] = value;
    updateNested(sectionKey, 'measurements', newMeas);
  };

  const addMeas = () => {
    const newMeas = [...(data.measurements || []), {}];
    updateNested(sectionKey, 'measurements', newMeas);
  };

  const removeMeas = (idx) => {
    const newMeas = [...(data.measurements || [])];
    newMeas.splice(idx, 1);
    updateNested(sectionKey, 'measurements', newMeas);
  };

  const handlePhotoUpload = (idx, e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map(file => {
        const uploadId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return {
          file,
          name: file.name,
          uploadId,
          preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null
        };
      });
      
      const newMeas = [...(data.measurements || [])];
      if (!newMeas[idx]) newMeas[idx] = {};
      newMeas[idx].photos = [...(newMeas[idx].photos || []), ...newFiles];
      updateNested(sectionKey, 'measurements', newMeas);
    }
  };

  const removePhoto = (rowIdx, photoIdx) => {
    const newMeas = [...(data.measurements || [])];
    if (newMeas[rowIdx] && newMeas[rowIdx].photos) {
      newMeas[rowIdx].photos = newMeas[rowIdx].photos.filter((_, i) => i !== photoIdx);
      updateNested(sectionKey, 'measurements', newMeas);
    }
  };

  const measurements = data.measurements || [{}];
  const lastRow = measurements[measurements.length - 1];
  const canAddRow = measurements.length === 0 || (lastRow && (
    lastRow.zone || lastRow.floor || lastRow.particulars || 
    lastRow.height || lastRow.width || lastRow.depth || 
    lastRow.qty || lastRow.notes || (lastRow.photos && lastRow.photos.length > 0)
  ));

  return (
    <Section title={title} expanded={expandedSection === sectionKey} onToggle={() => toggleSection(sectionKey)}>
      {children}
      
      <div className="flex justify-between items-center border-b pb-1 mb-2 mt-2">
        <h4 className="text-xs font-semibold text-slate-700">Measurements</h4>
        <div className="flex gap-2">
          <Button type="button" onClick={addMeas} disabled={!canAddRow} variant="outline" className={`text-[10px] py-1 h-7 px-3 ${!canAddRow ? 'opacity-50 cursor-not-allowed' : ''}`} size="sm">
            <Plus size={12} className="mr-1" /> Add Row
          </Button>
        </div>
      </div>
      
      <div className="overflow-x-auto pb-2 custom-scrollbar">
        <div className="flex items-center gap-2 px-2 mb-1 min-w-max">
          <div className="w-28 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Zone</div>
          <div className="w-28 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Floor</div>
          <div className="w-40 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Requirement</div>
          <div className="w-16 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">H(mm)</div>
          <div className="w-16 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">W(mm)</div>
          <div className="w-16 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">D(mm)</div>
          <div className="w-20 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Qty</div>
          <div className="w-40 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Notes</div>
          <div className="w-20 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase text-center">Photo</div>
          <div className="w-7 flex-shrink-0"></div>
        </div>
        
        <div className="space-y-2 min-w-max">
          {measurements.map((item, idx) => (
            <div key={idx} className="flex flex-col gap-1.5 p-1.5 bg-slate-50 border border-slate-100 rounded">
              <div className="flex items-center gap-2">
                <div className="w-28 flex-shrink-0">
                  <input type="text" placeholder="Zone" value={item.zone || ''} onChange={e => updateMeas(idx, 'zone', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-28 flex-shrink-0">
                  <input type="text" placeholder="Floor" value={item.floor || ''} onChange={e => updateMeas(idx, 'floor', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-40 flex-shrink-0">
                  <input type="text" placeholder="Requirement" value={item.particulars || ''} onChange={e => updateMeas(idx, 'particulars', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-16 flex-shrink-0">
                  <input type="number" placeholder="H" value={item.height || ''} onChange={e => updateMeas(idx, 'height', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-16 flex-shrink-0">
                  <input type="number" placeholder="W" value={item.width || ''} onChange={e => updateMeas(idx, 'width', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-16 flex-shrink-0">
                  <input type="number" placeholder="D" value={item.depth || ''} onChange={e => updateMeas(idx, 'depth', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-20 flex-shrink-0">
                  <input type="text" placeholder="Qty" value={item.qty || ''} onChange={e => updateMeas(idx, 'qty', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-40 flex-shrink-0">
                  <input type="text" placeholder="Notes" value={item.notes || ''} onChange={e => updateMeas(idx, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
                </div>
                <div className="w-20 flex-shrink-0">
                  <label 
                    className={`group flex items-center justify-center gap-1 cursor-pointer border ${(item.photos && item.photos.length > 0) ? 'border-green-300 bg-green-50 text-green-700 transition-colors hover:bg-green-100' : 'border-slate-200 hover:bg-slate-50 text-slate-700'} w-full py-1.5 rounded h-[28px] bg-white relative overflow-hidden`}
                    title="Upload More Photos"
                  >
                    {(item.photos && item.photos.length > 0) ? (
                      <>
                        <div className="flex items-center gap-1 group-hover:hidden">
                          <Camera size={14} />
                          <span className="text-[11px] font-bold">{item.photos.length}</span>
                        </div>
                        <div className="hidden group-hover:flex items-center gap-0.5">
                          <Plus size={14} />
                          <span className="text-[10px] font-bold">More</span>
                        </div>
                      </>
                    ) : (
                      <Camera size={16} />
                    )}
                    <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*,video/*" onChange={e => handlePhotoUpload(idx, e)} />
                  </label>
                </div>
                <div className="flex-shrink-0 flex items-center justify-center w-7">
                  <TableActionButton icon={Trash2} variant="danger" onClick={() => removeMeas(idx)} title="Remove Row" />
                </div>
              </div>
              
              {/* Thumbnails below the row */}
              {item.photos && item.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1.5 border-t border-slate-100 pl-1 mt-0.5">
                  {item.photos.map((photo, pIdx) => (
                    <div key={pIdx} className="relative w-12 h-12 rounded border border-slate-200 overflow-hidden bg-white group">
                      {photo.preview ? (
                        <img src={photo.preview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-50"><FileVideo className="w-5 h-5 text-slate-400" /></div>
                      )}
                      <button 
                        type="button" 
                        onClick={() => removePhoto(idx, pIdx)}
                        className="absolute -top-1 -right-1 bg-white border border-slate-200 rounded-full p-0.5 text-slate-500 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity z-10 scale-75"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

    </Section>
  );
};

export const SiteVisitWizard = ({ initialData = {}, onSubmit, onCancel, loading = false, lead, fullName, commonNotes, notesValue }) => {
  const [formData, setFormData] = useState(() => {
    // If we have an existing siteVisitData from initialData, map it to formData
    if (initialData.siteVisitData && initialData.siteVisitData.measurements) {
      const svData = initialData.siteVisitData;
      const mappedData = {
        rooms: svData.rooms || [],
        notes: svData.commonNotes || notesValue || ''
      };
      
      // Group measurements by room
      svData.measurements.forEach(meas => {
        if (!mappedData[meas.room]) {
          mappedData[meas.room] = { measurements: [] };
        }
        
        // Map photos
        const mappedPhotos = (meas.photos || []).map(photo => {
          const fileId = photo.fileId || photo._id;
          return {
            ...photo,
            fileId,
            preview: fileId ? `${BASE_URL}/api/verve-leads/site-visit/media/${fileId}` : null
          };
        });
        
        mappedData[meas.room].measurements.push({
          zone: meas.zone || '',
          floor: meas.floor || '',
          particulars: meas.particulars || '',
          height: meas.height || '',
          width: meas.width || '',
          depth: meas.depth || '',
          qty: meas.qty || '',
          notes: meas.notes || '',
          photos: mappedPhotos
        });
      });
      
      return mappedData;
    }

    const saved = localStorage.getItem(`siteVisitWizardData_${initialData._id}`);
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return { 
      rooms: [],
      ...initialData 
    };
  });

  const [availableRooms, setAvailableRooms] = useState([]);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await settingsRoomService.getRooms();
        if (res.success) {
          setAvailableRooms(res.data.filter(r => r.status === 'Active'));
        }
      } catch (error) {
        console.error("Failed to load rooms", error);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    if (initialData._id) {
      localStorage.setItem(`siteVisitWizardData_${initialData._id}`, JSON.stringify(formData));
    }
  }, [formData, initialData._id]);

  const [expandedSection, setExpandedSection] = useState('rooms');
  const [step, setStep] = useState('edit');
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);

  const handleSaveNewRoom = async (roomData) => {
    try {
      const res = await settingsRoomService.createRoom(roomData);
      if (res.success) {
        toast.success("Room created successfully");
        setAvailableRooms(prev => [...prev, res.data]);
        
        // Auto-select the newly created room
        const currentRooms = formData.rooms || [];
        if (!currentRooms.includes(res.data.name)) {
          updateForm('rooms', [...currentRooms, res.data.name]);
        }
        setExpandedSection(res.data.name);
        setIsAddRoomModalOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create room");
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateNested = (section, key, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: value }
    }));
  };

  const handleRoomToggle = (room) => {
    const current = formData.rooms || [];
    if (current.includes(room)) {
      updateForm('rooms', current.filter(r => r !== room));
    } else {
      updateForm('rooms', [...current, room]);
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (initialData._id) {
      localStorage.removeItem(`siteVisitWizardData_${initialData._id}`);
    }
    
    // Extract all files from nested measurement rows and prepare file objects
    const siteVisitFiles = [];
    formData.rooms?.forEach(room => {
      const measurements = formData[room]?.measurements || [];
      measurements.forEach(m => {
        if (m.photos && m.photos.length > 0) {
          m.photos.forEach(p => {
            // Include the uploadId with the file object or just pass it to parent
            siteVisitFiles.push({
              file: p.file,
              uploadId: p.uploadId
            });
          });
        }
      });
    });

    onSubmit({ ...formData, siteVisitFiles });
  };

  // Collect all photos for the preview screen
  const allPhotos = [];
  formData.rooms?.forEach(room => {
    const measurements = formData[room]?.measurements || [];
    measurements.forEach((m, idx) => {
      if (m.photos && m.photos.length > 0) {
        m.photos.forEach(photoObj => {
          allPhotos.push({
            room,
            zone: m.zone || `Row ${idx + 1}`,
            particulars: m.particulars || 'N/A',
            file: photoObj.file,
            filename: photoObj.filename || (photoObj.file ? photoObj.file.name : 'Unknown File'),
            preview: photoObj.preview
          });
        });
      }
    });
  });

  if (step === 'preview') {
    return (
      <div className="flex flex-col gap-5 p-1">
        <div className="space-y-4 h-[450px] overflow-y-auto pr-2 custom-scrollbar">
          <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-widest mb-3 border-b border-slate-100 pb-2">
            Preview Site Visit Details
          </h3>
          
          <div className="space-y-4">
            {/* 
            <div>
              <h4 className="text-[10px] font-bold text-slate-500 uppercase">Selected Rooms</h4>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {formData.rooms?.length > 0 ? formData.rooms.map(room => (
                  <span key={room} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-[11px] font-semibold">{room}</span>
                )) : <span className="text-xs text-slate-400">No rooms selected</span>}
              </div>
            </div>
            */}

            {formData.rooms?.map(room => {
              const measurements = formData[room]?.measurements || [];
              if (measurements.length === 0 || Object.keys(measurements[0]).length === 0) return null;
              
              return (
                <div key={room} className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
                  <h4 className="text-[11px] font-bold text-slate-700 uppercase mb-2">{room} Measurements ({measurements.length})</h4>
                  <div className="space-y-2">
                    {measurements.map((m, idx) => (
                      <div key={idx} className="text-[11px] text-slate-600 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 bg-white p-2 border border-slate-100 rounded">
                        <div><span className="font-semibold">Zone:</span> {m.zone || '-'}</div>
                        <div><span className="font-semibold">Floor:</span> {m.floor || '-'}</div>
                        <div><span className="font-semibold">Requirement:</span> {m.particulars || '-'}</div>
                        <div><span className="font-semibold">Dimensions:</span> {m.height||'-'}x{m.width||'-'}x{m.depth||'-'}</div>
                        <div><span className="font-semibold">Qty:</span> {m.qty || '-'}</div>
                        <div><span className="font-semibold">Notes:</span> {m.notes || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}



            {/* Media Preview */}
            {allPhotos.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-1.5">Site Visit Images & Media ({allPhotos.length})</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {allPhotos.map((photo, idx) => (
                     <div key={idx} className="border border-slate-200 rounded-lg p-2 bg-slate-50 flex flex-col items-center gap-2">
                      <div className="w-full h-20 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                        {photo.preview ? (
                          <img src={photo.preview} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          photo.file?.type?.startsWith('video/') ? <FileVideo className="w-6 h-6 text-slate-400" /> : <File className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      <div className="w-full text-center">
                        <div className="text-[10px] font-bold text-slate-700 truncate w-full" title={`${photo.room} - ${photo.particulars}`}>
                          {photo.room} - {photo.particulars}
                        </div>
                        <div className="text-[9px] text-slate-500 truncate w-full" title={photo.filename}>
                          {photo.filename}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-3 border-t border-slate-100 mt-2">
          <button
            type="button"
            onClick={() => setStep('edit')}
            className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-bold transition-all duration-300 shadow-sm"
          >
            Back to Edit
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out shadow-sm flex items-center justify-center gap-2"
          >
            {loading ? "Processing..." : "Confirm & Submit"}
            {!loading && <Check size={14} />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-1">
      <div className="space-y-4 h-[450px] overflow-y-auto pr-2 custom-scrollbar">
        <div className="mb-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              01. SELECT ROOMS REQUIRED
            </h3>
            <button
              type="button"
              onClick={() => setIsAddRoomModalOpen(true)}
              className="flex items-center gap-1 text-[10px] font-bold bg-slate-900 text-white px-2.5 py-1 rounded hover:bg-black transition-colors shadow-sm"
            >
              <Plus size={12} /> Add Room
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {availableRooms.map(roomObj => {
              const room = roomObj.name;
              const isSelected = formData.rooms?.includes(room);
              return (
                <button
                  key={room}
                  type="button"
                  onClick={() => {
                    handleRoomToggle(room);
                    if (!isSelected) {
                      setExpandedSection(room);
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold transition-all duration-200 border shadow-sm ${
                    isSelected 
                      ? 'bg-slate-900 border-slate-900 text-white' 
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {isSelected ? <Check size={14} className="text-white" /> : <Plus size={14} className="text-slate-400" />}
                  {room}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dynamic Measurement Sections */}
        {(formData.rooms || []).map(selectedRoomName => (
          <MeasurementSection
            key={selectedRoomName}
            title={`${selectedRoomName} Measurement Form`}
            expandedSection={expandedSection}
            toggleSection={toggleSection}
            data={formData[selectedRoomName] || {}}
            sectionKey={selectedRoomName}
            updateNested={updateNested}
          />
        ))}



        <div className="mt-4">
          {commonNotes}
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-3 border-t border-slate-100 mt-2">
        <button
          type="button"
          onClick={() => {
            if (initialData._id) {
              localStorage.removeItem(`siteVisitWizardData_${initialData._id}`);
            }
            onCancel();
          }}
          className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-bold transition-all duration-300 shadow-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setStep('preview')}
          className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out shadow-sm border border-slate-900 flex items-center justify-center gap-2"
        >
          Preview Details
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
        </button>
      </div>

      {isAddRoomModalOpen && (
        <AddRoomModal
          isOpen={isAddRoomModalOpen}
          onClose={() => setIsAddRoomModalOpen(false)}
          onSave={handleSaveNewRoom}
        />
      )}
    </div>
  );
};
